import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { MRT_ColumnDef, MaterialReactTable } from "material-react-table";
import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { monospaceStyle } from "../../theme";
import { CountryFlag } from "../common/CountryFlag";
import DerivedTag from "../Detail/DerivedTag";
import { useHeadingSlot } from "../Detail/DetailSections";
import EntityLink from "../Detail/EntityLink";
import RowToggle, { NoToggle } from "../Detail/RowToggle";
import SearchField from "../Detail/SearchField";
import SourcesChip from "../Detail/SourcesChip";
import { columnHeader, quiet, tableFrame } from "../Detail/claimCells";
import { codeWords } from "../Detail/format";
import { RelationRow, rolesOf } from "./productRelations";

// The columns a relation table may show besides the entity, the relation
// and the sources, in the order shown. A column no row fills is left out.
export type RelationColumn =
  | "category"
  | "detail"
  | "status"
  | "severity"
  | "period"
  | "date"
  | "address"
  | "countries"
  | "quantity"
  | "latest";

export type RelationFilter = "relation" | "type";

export interface ProductRelationTableProps {
  rows: RelationRow[];
  // The head of the first column: what the linked entities are
  // ("Programme", "Product")
  entityHeading: string;
  // The further columns, in order
  columns: RelationColumn[];
  // Heads that differ from the default of a column, e.g. "Role" for the
  // detail of a component, "Date" for the period of an incident
  headings?: Partial<Record<RelationColumn | "relation", string>>;
  // What the chips above the table pick by: the relation (the default), or
  // the types of the linked entities' own records
  filterBy?: RelationFilter;
  // Show the relation column with one relation too, where the section does
  // not say it ("Integrated by" among organizations)
  showRelation?: boolean;
  // A plain table for a printed page: every row, the summary as a column,
  // no sources, nothing to filter or open
  print?: boolean;
}

// Rows from which the table offers a search, and rows on one page
export const SEARCH_FROM = 9;
export const PAGE_ROWS = 10;

const DEFAULT_HEADINGS: Record<RelationColumn | "relation", string> = {
  relation: "Role",
  category: "Category",
  detail: "Detail",
  status: "Status",
  severity: "Severity",
  period: "Period",
  date: "Date",
  address: "Location",
  countries: "Country",
  quantity: "Quantity",
  latest: "Latest source",
};

// Incidents from the least grave to the gravest; an unknown grade sorts
// below all
const SEVERITY_ORDER = ["minor", "moderate", "major", "critical"];

// A period as it reads: "2026-06 – 2029", "since 2015" without an end, one
// date when it began and ended the same day
const periodText = (row: RelationRow): string => {
  if (!row.start && !row.end) {
    return "";
  }
  if (row.start && !row.end) {
    return `since ${row.start}`;
  }
  if (row.start === row.end) {
    return row.start ?? "";
  }
  return `${row.start ?? "?"} – ${row.end}`;
};

// The date of an event: the day it began, and the day it ended where that
// is another. An event without an end is not "since" anything.
const dateText = (row: RelationRow): string =>
  row.start && row.end && row.start !== row.end
    ? `${row.start} – ${row.end}`
    : (row.start ?? row.end ?? "");

// A facility's address where it says more than its country: the address of
// one placed no finer only names the country again
const addressText = (row: RelationRow): string =>
  row.locationPrecision && row.locationPrecision !== "country" ? (row.address ?? "") : "";

// Whether a row fills a column
const fills = (row: RelationRow, column: RelationColumn): boolean => {
  switch (column) {
    case "category":
      return Boolean(row.category);
    case "detail":
      return Boolean(row.detail);
    case "status":
      return Boolean(row.status?.length);
    case "severity":
      return Boolean(row.severity);
    case "period":
    case "date":
      return Boolean(row.start || row.end);
    case "address":
      return Boolean(addressText(row));
    case "countries":
      return Boolean(row.countries?.length);
    case "quantity":
      return Boolean(row.quantityText);
    case "latest":
      return Boolean(row.latest);
  }
};

// The words a row is searched by
const searchText = (row: RelationRow): string =>
  [
    row.entity.name,
    row.relation,
    row.summary,
    row.detail,
    row.category?.name,
    ...(row.types ?? []),
    ...(row.status ?? []),
    ...(row.countries ?? []),
    row.address,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

// The values a row is picked by from the chips: each of its relations, or
// its types in words
const facetsOf = (row: RelationRow, filterBy: RelationFilter): string[] =>
  filterBy === "type" ? (row.types ?? []).map(codeWords) : rolesOf(row);

// Whether a row has more to show than its cells: the relation in a
// sentence. Its sources and their quotes are in its sources chip.
const opens = (row: RelationRow): boolean => Boolean(row.summary);

const quietSmall = { ...quiet, fontSize: "0.75rem" } as const;

// The height (px) of a closed row, with one line in its cells and with the
// line of types beneath the name, so that every row of a table and every
// page of it are the same height
const ROW_HEIGHT = 40;
const TWO_LINE_ROW_HEIGHT = 56;
// The height (px) of the row of column heads
const HEAD_HEIGHT = 40;

// One line of a cell on the screen, cut short with an ellipsis and whole on
// hover; on a printed page the text wraps
const Line: React.FC<{
  title?: string | null;
  wrap?: boolean;
  sx?: Record<string, unknown>;
  children: React.ReactNode;
}> = ({ title, wrap = false, sx, children }) => (
  <Box
    title={wrap ? undefined : (title ?? undefined)}
    sx={{
      minWidth: 0,
      ...(wrap
        ? { whiteSpace: "normal" }
        : { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }),
      ...sx,
    }}
  >
    {children}
  </Box>
);

const typesText = (row: RelationRow): string =>
  (row.types ?? []).map(codeWords).join(", ");

// The name of the linked entity, its derivation, and its types beneath.
// Where the table shows types (`typed`) a row without any keeps the line,
// empty, so that its height is the others'.
const EntityCell: React.FC<{
  row: RelationRow;
  typed: boolean;
  wrap?: boolean;
}> = ({ row, typed, wrap = false }) => (
  <Box sx={{ minWidth: 0 }}>
    <Line title={row.entity.name} wrap={wrap}>
      <EntityLink id={row.entity.id} type={row.entity.type} name={row.entity.name} />
      {row.derived && (
        <>
          {" "}
          <DerivedTag basis={row.derived.basis} />
        </>
      )}
    </Line>
    {typed && (
      <Line title={typesText(row)} wrap={wrap} sx={quietSmall}>
        {typesText(row) || " "}
      </Line>
    )}
  </Box>
);

// A stated quantity: its number in monospace, then the words that go with it
const QuantityCell: React.FC<{ row: RelationRow }> = ({ row }) => {
  if (!row.quantityText) {
    return null;
  }
  const match = /^(\d[\d.,]*)(.*)$/.exec(row.quantityText);
  return match ? (
    <>
      <Box component="span" sx={monospaceStyle}>
        {match[1]}
      </Box>
      {match[2]}
    </>
  ) : (
    <>{row.quantityText}</>
  );
};

const CountriesCell: React.FC<{ row: RelationRow; wrap?: boolean }> = ({
  row,
  wrap = false,
}) => (
  <Box
    title={wrap ? undefined : (row.countries ?? []).join(", ")}
    sx={{
      display: "flex",
      flexWrap: wrap ? "wrap" : "nowrap",
      overflow: "hidden",
      gap: 0.75,
    }}
  >
    {(row.countries ?? []).map((code) => (
      <Box
        key={code}
        component="span"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
      >
        <CountryFlag code={code} size={12} round />
        <Box component="span" sx={monospaceStyle}>
          {code}
        </Box>
      </Box>
    ))}
  </Box>
);

// What a cell of a further column shows: one line on the screen (`wrap`
// false), the whole text on a printed page
const cellOf = (
  row: RelationRow,
  column: RelationColumn,
  wrap = false,
): React.ReactNode => {
  switch (column) {
    case "category":
      return row.category ? (
        <Line title={row.category.name} wrap={wrap}>
          <EntityLink
            id={row.category.id}
            type={row.category.type}
            name={row.category.name}
          />
        </Line>
      ) : null;
    case "detail":
      return (
        <Line title={row.detail} wrap={wrap} sx={quiet}>
          {row.detail}
        </Line>
      );
    case "status": {
      const status = (row.status ?? []).map(codeWords).join(", ");
      return (
        <Line title={status} wrap={wrap}>
          {status}
        </Line>
      );
    }
    case "severity":
      return row.severity ? codeWords(row.severity) : null;
    case "period":
      return (
        <Line title={periodText(row)} sx={monospaceStyle}>
          {periodText(row)}
        </Line>
      );
    case "date":
      return (
        <Line title={dateText(row)} sx={monospaceStyle}>
          {dateText(row)}
        </Line>
      );
    case "address":
      return (
        <Line title={addressText(row)} wrap={wrap}>
          {addressText(row)}
        </Line>
      );
    case "countries":
      return <CountriesCell row={row} wrap={wrap} />;
    case "quantity":
      return (
        <Line title={row.quantityText} wrap={wrap}>
          <QuantityCell row={row} />
        </Line>
      );
    case "latest":
      return (
        <Box component="span" sx={{ ...monospaceStyle, ...quiet, whiteSpace: "nowrap" }}>
          {row.latest}
        </Box>
      );
  }
};

// What a further column is sorted by
const sortValue = (row: RelationRow, column: RelationColumn): string | number => {
  switch (column) {
    case "category":
      return row.category?.name ?? "";
    case "detail":
      return row.detail ?? "";
    case "status":
      return (row.status ?? []).join(", ");
    case "severity":
      return SEVERITY_ORDER.indexOf(row.severity ?? "");
    case "period":
    case "date":
      return row.start ?? row.end ?? "";
    case "address":
      return addressText(row);
    case "countries":
      return (row.countries ?? []).join(" ");
    case "quantity":
      return row.quantity ?? -1;
    case "latest":
      return row.latest ?? "";
  }
};

// What an opened row shows: the relation in a sentence
const RowDetail: React.FC<{ row: RelationRow }> = ({ row }) => (
  <Typography variant="body2" sx={{ whiteSpace: "normal", py: 0.5, maxWidth: 760 }}>
    {row.summary}
  </Typography>
);

// The plain table of a printed page: every row, the summary as a column
const PrintTable: React.FC<{
  rows: RelationRow[];
  entityHeading: string;
  columns: RelationColumn[];
  headings: Record<RelationColumn | "relation", string>;
}> = ({ rows, entityHeading, columns, headings }) => {
  const hasSummary = rows.some((row) => row.summary);
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {[entityHeading, headings.relation, ...columns.map((column) => headings[column]), ...(hasSummary ? ["Summary"] : [])].map((heading) => (
              <TableCell key={heading} sx={columnHeader}>
                {heading}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key} sx={{ verticalAlign: "top" }}>
              <TableCell>
                <EntityCell row={row} typed={false} wrap />
                {row.types && row.types.length > 0 && (
                  <Box sx={quietSmall}>{typesText(row)}</Box>
                )}
              </TableCell>
              <TableCell sx={quiet}>{row.relation}</TableCell>
              {columns.map((column) => (
                <TableCell key={column}>{cellOf(row, column, true)}</TableCell>
              ))}
              {hasSummary && (
                <TableCell sx={{ ...quiet, fontSize: "0.8125rem" }}>{row.summary}</TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/**
 * The entities of one kind linked to a product, as a table: the entity with
 * its types, how it is linked, the further columns its kind has, and a
 * chip that lists its sources. Above it, a chip per relation (or per type,
 * with `filterBy` "type") picks the rows shown, a chip sets the derived links aside, and a longer table
 * offers a search. Every column sorts; a row opens on its sentence. A
 * closed row is one height whatever its text, cut to one
 * line, and a paged table keeps the height of a full page.
 *
 * A further column no row fills is left out; renders nothing without a row.
 */
const ProductRelationTable: React.FC<ProductRelationTableProps> = ({
  rows,
  entityHeading,
  columns,
  headings,
  filterBy = "relation",
  showRelation = false,
  print = false,
}) => {
  const [picked, setPicked] = useState<string[]>([]);
  const [statedOnly, setStatedOnly] = useState(false);
  const [search, setSearch] = useState("");
  const headingSlot = useHeadingSlot();

  const allHeadings = { ...DEFAULT_HEADINGS, ...headings };
  const shownColumns = columns.filter((column) =>
    rows.some((row) => fills(row, column)),
  );

  // The relations, in the order of the rows
  const relations = useMemo(
    () => Array.from(new Set(rows.flatMap(rolesOf))),
    [rows],
  );
  // The values of the chips, each with its count: the relations in the
  // order of the rows, the types the most frequent first
  const facets = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of rows) {
      for (const facet of facetsOf(row, filterBy)) {
        counts.set(facet, (counts.get(facet) ?? 0) + 1);
      }
    }
    const entries = Array.from(counts);
    return filterBy === "type"
      ? entries.sort(
          ([left, leftCount], [right, rightCount]) =>
            rightCount - leftCount || left.localeCompare(right),
        )
      : entries;
  }, [rows, filterBy]);
  const hasDerived = rows.some((row) => row.derived);
  // Whether a name has its line of types beneath: the same for every row
  // and page of the table, whatever the filters keep
  const typed = rows.some((row) => row.types && row.types.length > 0);
  const rowHeight = typed ? TWO_LINE_ROW_HEIGHT : ROW_HEIGHT;

  const filtered = useMemo(() => {
    const words = search.trim().toLowerCase();
    return rows.filter(
      (row) =>
        (picked.length === 0 ||
          facetsOf(row, filterBy).some((facet) => picked.includes(facet))) &&
        (!statedOnly || !row.derived) &&
        (!words || searchText(row).includes(words)),
    );
  }, [rows, filterBy, picked, statedOnly, search]);

  const tableColumns = useMemo<MRT_ColumnDef<RelationRow>[]>(
    () => [
      {
        id: "entity",
        header: entityHeading,
        accessorFn: (row) => (row.entity.name ?? "").toLowerCase(),
        size: 220,
        Cell: ({ row }) => <EntityCell row={row.original} typed={typed} />,
      },
      // One relation needs no column, unless asked: the section says it
      ...(relations.length > 1 || showRelation
        ? [
            {
              id: "relation",
              header: allHeadings.relation,
              accessorFn: (row: RelationRow) => row.relation,
              size: 140,
              Cell: ({ row }: { row: { original: RelationRow } }) => (
                <Line title={row.original.relation} sx={quiet}>
                  {row.original.relation}
                </Line>
              ),
            },
          ]
        : []),
      ...shownColumns.map(
        (column): MRT_ColumnDef<RelationRow> => ({
          id: column,
          header: allHeadings[column],
          accessorFn: (row) => sortValue(row, column),
          sortDescFirst: column === "quantity" || column === "latest",
          size: column === "detail" || column === "category" ? 180 : 120,
          Cell: ({ row }) => cellOf(row.original, column),
        }),
      ),
      {
        id: "sources",
        header: "Sources",
        accessorFn: (row) => row.sources.length,
        sortDescFirst: true,
        size: 110,
        muiTableHeadCellProps: { align: "right" },
        muiTableBodyCellProps: { align: "right" },
        Cell: ({ row }) => <SourcesChip sources={row.original.sources} />,
      },
    ],
    // The headings are rebuilt each render from stable props
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entityHeading, relations.length, showRelation, typed, shownColumns.join(",")],
  );

  if (rows.length === 0) {
    return null;
  }
  if (print) {
    return (
      <PrintTable
        rows={rows}
        entityHeading={entityHeading}
        columns={shownColumns}
        headings={allHeadings}
      />
    );
  }

  const toggle = (facet: string) =>
    setPicked((current) =>
      current.includes(facet)
        ? current.filter((value) => value !== facet)
        : [...current, facet],
    );

  // The search of a longer table, in the line of the heading above where it
  // has one, so that no line of its own stands between them
  const searchField = rows.length >= SEARCH_FROM && (
    <SearchField
      value={search}
      onChange={setSearch}
      label={`Search ${entityHeading.toLowerCase()} links`}
    />
  );
  const searchInToolbar = searchField && !headingSlot;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {searchField && headingSlot && createPortal(searchField, headingSlot)}
      {(facets.length > 1 || hasDerived || searchInToolbar) && (
        <Box
          role="toolbar"
          aria-label={`Filter ${entityHeading.toLowerCase()} links`}
          sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}
        >
          {facets.length > 1 &&
            facets.map(([facet, count]) => (
              <Chip
                key={facet}
                label={`${facet} ${count}`}
                size="small"
                clickable
                color={picked.includes(facet) ? "primary" : "default"}
                variant={picked.includes(facet) ? "filled" : "outlined"}
                aria-pressed={picked.includes(facet)}
                onClick={() => toggle(facet)}
              />
            ))}
          {hasDerived && (
            <Chip
              label="Stated only"
              size="small"
              clickable
              color={statedOnly ? "primary" : "default"}
              variant={statedOnly ? "filled" : "outlined"}
              aria-pressed={statedOnly}
              onClick={() => setStatedOnly((value) => !value)}
            />
          )}
          {searchInToolbar && searchField}
        </Box>
      )}
      <MaterialReactTable
        columns={tableColumns}
        data={filtered}
        getRowId={(row) => row.key}
        enableSorting
        enableMultiSort={false}
        enableColumnActions={false}
        enableColumnFilters={false}
        enableGlobalFilter={false}
        enableTopToolbar={false}
        enablePagination={filtered.length > PAGE_ROWS}
        enableBottomToolbar={filtered.length > PAGE_ROWS}
        renderDetailPanel={({ row }) =>
          opens(row.original) ? <RowDetail row={row.original} /> : null
        }
        getRowCanExpand={(row) => opens(row.original)}
        enableExpandAll={false}
        // A narrow column with the toggle every table of the view opens a
        // row with; a row with nothing to open keeps its space
        displayColumnDefOptions={{
          "mrt-row-expand": {
            header: "",
            size: 36,
            Header: () => null,
            // The table types a display column's row loosely
            Cell: ({ row }) =>
              opens(row.original as RelationRow) ? (
                <RowToggle
                  open={row.getIsExpanded()}
                  label={row.getIsExpanded() ? "Collapse" : "Expand"}
                  onToggle={() => row.toggleExpanded()}
                />
              ) : (
                <NoToggle />
              ),
            muiTableHeadCellProps: { sx: { pr: 0 } },
            muiTableBodyCellProps: { sx: { pr: 0 } },
          },
        }}
        // A pager of ten rows a page, with no choice of page size
        muiTablePaginationProps={{
          rowsPerPageOptions: [PAGE_ROWS],
          showFirstButton: false,
          showLastButton: false,
        }}
        muiBottomToolbarProps={{
          sx: { backgroundColor: "background.raised", boxShadow: "none" },
        }}
        initialState={{
          density: "compact",
          pagination: { pageIndex: 0, pageSize: PAGE_ROWS },
        }}
        localization={{ noRecordsToDisplay: "No links match the filters" }}
        muiTablePaperProps={{ elevation: 0, sx: tableFrame }}
        muiTableHeadCellProps={{ sx: columnHeader }}
        // A closed row is one height; the row of an opened detail grows
        // with it
        muiTableBodyRowProps={({ isDetailPanel }) =>
          isDetailPanel
            ? { hover: false }
            : { hover: false, sx: { height: rowHeight } }
        }
        // A paged table keeps the height of a full page, so that turning to
        // a shorter last page moves nothing below it
        muiTableContainerProps={{
          sx: {
            minHeight:
              filtered.length > PAGE_ROWS
                ? HEAD_HEIGHT + PAGE_ROWS * rowHeight
                : undefined,
          },
        }}
        muiTableProps={{
          sx: {
            // The columns keep their widths, so that a long text is cut to
            // its line instead of widening its column
            tableLayout: "fixed",
            "& .MuiTableHead-root .MuiTableCell-root": {
              backgroundColor: "background.raised",
            },
            "& .MuiTableBody-root .MuiTableRow-root": {
              backgroundColor: "background.raised",
            },
          },
        }}
      />
    </Box>
  );
};

export default ProductRelationTable;
